// SERPsizer web UI entry. Each screen has its own path (/, /bulk, /settings) and the server sends
// that path's pre-rendered HTML (scripts/prerender-web.ts). This script takes the page over: it
// swaps the pre-rendered screen for the live one once Google's fonts load (Settings needs no fonts,
// so it goes live at once), and moves between screens with pushState instead of reloading.
import './ui/basecoat';
import { loadAllFonts } from './engine';
import { btn, clear, h, icon, ICONS } from './ui/dom';
import { applyTheme, profileSet, state, updateSettings, type Theme } from './ui/state';
import { loadingView, makeView } from './ui/shell';
import { ROUTES, SITE, routeFor, routeOf, type Tab } from './routes';

applyTheme(state.settings.theme);
matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => applyTheme(state.settings.theme));

const main = document.getElementById('main')!;
const nav = document.querySelector<HTMLElement>('nav.topnav')!;

const THEMES: [Theme, string, string[]][] = [['system', 'System theme', ICONS.monitor], ['light', 'Light theme', ICONS.sun], ['dark', 'Dark theme', ICONS.moon]];
const themeGroup = h('div', { class: 'theme', role: 'radiogroup', 'aria-label': 'Theme' },
  THEMES.map(([t, label, ic]) => {
    const b = h('button', { type: 'button', role: 'radio', class: 'theme-btn', 'aria-label': label, title: label, 'aria-checked': String(state.settings.theme === t) }, icon(ic, 15));
    b.addEventListener('click', () => {
      updateSettings({ theme: t });
      applyTheme(t);
      themeGroup.querySelectorAll('button').forEach(x => x.setAttribute('aria-checked', String(x === b)));
    });
    return b;
  }));

document.querySelector('.topbar-right')!.append(themeGroup);
// Basecoat's toast container (web/src/ui/toast.ts).
document.body.append(h('div', { id: 'toaster', class: 'toaster' }));

// Old links like /#bulk land on /bulk.
function fromHash(): boolean {
  const tab = location.hash.slice(1);
  if (!ROUTES.some(r => r.tab === tab)) return false;
  history.replaceState(null, '', routeOf(tab as Tab).path + location.search);
  return true;
}
fromHash();

const views = new Map<Tab, HTMLElement>();
// The screen the server rendered, inert until the live one replaces it.
let pre = main.querySelector<HTMLElement>('[data-prerendered]');
if (pre && pre.dataset.prerendered !== 'check') pre.inert = true;
const status = h('div', { class: 'loading', hidden: true });
main.append(status);
let ready = false;
let failure: unknown = null;

function setMeta(sel: string, attr: string, value: string | null) {
  let el = document.head.querySelector<HTMLElement>(sel);
  if (value === null) { el?.remove(); return; }
  if (!el) {
    el = sel.startsWith('link') ? h('link', { rel: 'canonical' }) : h('meta', { name: 'robots' });
    document.head.append(el);
  }
  el.setAttribute(attr, value);
}

// Check's intro and long-form content are only in the HTML for /; fetch them when another path was
// the landing page. Other pages keep them hidden.
let checkContent: Promise<void> | null = null;
function loadCheckContent(): Promise<void> {
  if (document.querySelector('.intro')) return Promise.resolve();
  return checkContent ??= fetch('/').then(r => r.text()).then(html => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const intro = doc.querySelector('.intro'), about = doc.querySelector('.about');
    if (intro) main.before(document.importNode(intro, true));
    if (about) main.after(document.importNode(about, true));
    showCheckContent(routeFor(location.pathname)?.tab === 'check');
  }).catch(() => { checkContent = null; });
}
function showCheckContent(on: boolean) {
  document.querySelectorAll<HTMLElement>('[data-check-only]').forEach(el => el.hidden = !on);
}

let lastPath = location.pathname;

function route() {
  const r = routeFor(location.pathname);
  const tab = r?.tab ?? null;
  clear(nav, ROUTES.map(x => h('a', { href: x.path, class: 'tab', 'aria-current': x.tab === tab ? 'page' : null }, x.label)));
  if (!r) return; // 404 page: the server's HTML stands.

  showCheckContent(r.tab === 'check');
  if (r.tab === 'check') loadCheckContent();
  document.title = r.title;
  setMeta('meta[name="description"]', 'content', r.description);
  setMeta('meta[property="og:title"]', 'content', r.title);
  setMeta('meta[property="og:description"]', 'content', r.description);
  setMeta('meta[property="og:url"]', 'content', SITE + r.path);
  setMeta('link[rel="canonical"]', 'href', SITE + r.path);
  setMeta('meta[name="robots"]', 'content', r.noindex ? 'noindex' : null);

  if (!views.has(r.tab) && (ready || r.tab === 'settings')) {
    const el = makeView(r.tab);
    views.set(r.tab, el);
    if (pre?.dataset.prerendered === r.tab) { pre.replaceWith(el); pre = null; }
    else status.before(el);
  }
  for (const [k, el] of views) el.hidden = k !== r.tab;
  const showPre = !!pre && pre.dataset.prerendered === r.tab && !failure;
  if (pre) pre.hidden = !showPre;
  const waiting = !views.has(r.tab) && !showPre;
  status.hidden = !waiting;
  if (waiting) {
    if (failure) {
      const retry = h('button', btn(), 'Retry');
      retry.addEventListener('click', boot);
      clear(status,
        h('p', { class: 'empty-title' }, 'Couldn’t load the fonts Google uses'),
        h('p', { class: 'muted' }, 'SERPsizer measures text in the exact font files Google serves, so it won’t show results with fallback fonts. Check your connection to fonts.gstatic.com and try again.'),
        h('p', { class: 'muted mono' }, failure instanceof Error ? failure.message : String(failure)),
        retry);
      status.setAttribute('role', 'alert');
    } else {
      clear(status, ...loadingView().childNodes);
      status.setAttribute('role', 'status');
    }
  }
}

/** Route after the path changed in place, and count it as a page view for Google Tag Manager. */
function navigated() {
  if (location.pathname === lastPath) return;
  lastPath = location.pathname;
  route();
  const w = window as unknown as { dataLayer?: unknown[] };
  (w.dataLayer ??= []).push({ event: 'virtual_page_view', page_path: location.pathname + location.search, page_location: location.href, page_title: document.title });
}

document.addEventListener('click', e => {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
  if (!a || a.target || a.hasAttribute('download') || a.origin !== location.origin) return;
  // Static pages and the 404 page aren't app screens: leave them with a normal page load.
  if (!routeFor(location.pathname)) return;
  const r = routeFor(a.pathname);
  if (!r || (a.pathname === location.pathname && a.hash)) return;
  e.preventDefault();
  if (r.path === location.pathname) return;
  history.pushState(null, '', r.path + a.search);
  navigated();
  window.scrollTo(0, 0);
});
window.addEventListener('popstate', navigated);
window.addEventListener('hashchange', () => { if (fromHash()) navigated(); });

async function boot() {
  failure = null;
  route();
  try {
    await loadAllFonts(profileSet.fonts);
    ready = true;
  } catch (e) {
    failure = e;
  }
  route();
}

boot();
